import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock, RefreshCw, CheckCircle2, Copy, ShieldCheck, AlertTriangle,
  Loader2, ArrowUpRight, Sparkles, UserPlus, Mail, Banknote, ChevronDown,
} from "lucide-react";
import SEO from "@/components/SEO";
import { formatApiError } from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import OrderTimeline from "@/components/OrderTimeline";
import ChangeToDeliveryModal from "@/components/tracker/ChangeToDeliveryModal";
import { track, EVENT } from "@/lib/analytics";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const STATUS_META = {
  awaiting_payment:  { label: "Awaiting Payment",  tone: "yellow" },
  payment_submitted: { label: "Awaiting Payment Confirmation", tone: "blue" },
  payment_verified:  { label: "Payment Confirmed", tone: "green" },
  preparing:         { label: "Preparing Order",   tone: "green" },
  ready:             { label: "Ready for Pickup",  tone: "green" },
  shipped:           { label: "Shipped",           tone: "green" },
  completed:         { label: "Completed",         tone: "green" },
  cancelled:         { label: "Cancelled",         tone: "red" },
};

// Iter J P1 — visual pipeline for the customer tracking page. Extracted
// into a shared component (`/components/OrderTimeline.jsx`) so the
// logged-in dashboard order-detail page renders the same widget.

function usdFmt(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return { expired: true, label: "—", diffMs: 0 };
  const target = new Date(iso).getTime();
  const diffMs = target - now;
  const expired = diffMs <= 0;
  const abs = Math.abs(diffMs);
  const hh = Math.floor(abs / 3600000);
  const mm = Math.floor((abs % 3600000) / 60000);
  const ss = Math.floor((abs % 60000) / 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return { expired, diffMs, label: `${pad(hh)}:${pad(mm)}:${pad(ss)}` };
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status, tone: "gray" };
  const toneClasses = {
    yellow: "bg-[var(--panini-yellow)] text-[var(--ink)] border-[var(--ink)]",
    blue:   "bg-blue-50 text-blue-900 border-blue-900",
    green:  "bg-emerald-50 text-emerald-900 border-emerald-900",
    red:    "bg-red-50 text-red-900 border-red-900",
    gray:   "bg-neutral-100 text-[var(--ink)] border-[var(--ink)]",
  };
  return (
    <span
      data-testid="order-status-pill"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 font-bold text-xs uppercase tracking-wide ${toneClasses[meta.tone]}`}
    >
      <span className="w-2 h-2 rounded-full bg-current opacity-70" />
      {meta.label}
    </span>
  );
}

function CopyChip({ value, label, testid }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        toast.success(`${label} copied`);
      }}
      className="group inline-flex items-center gap-1 px-2 py-1 border border-[var(--ink)] rounded bg-white hover:bg-[var(--panini-yellow-soft)] transition"
      aria-label={`Copy ${label}`}
    >
      {copied ? <CheckCircle2 size={12} className="text-emerald-700" /> : <Copy size={12} className="opacity-60 group-hover:opacity-100" />}
      <span className="text-[10px] uppercase tracking-widest font-bold">Copy</span>
    </button>
  );
}

export default function OrderTracker() {
  const { public_id } = useParams();
  const [sp] = useSearchParams();
  const magicToken = sp.get("t") || "";
  const [order, setOrder] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/orders/${public_id}`, { params: { t: magicToken } }),
        axios.get(`${API}/orders-payment-instructions`),
      ]);
      setOrder(r1.data);
      setInstructions(r2.data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Unable to load your order.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [public_id, magicToken]);

  // Phase 1d — email CTA handler. Save-progress emails include three
  // deep-links that echo back to the tracker with `?decision=partial|wait`.
  // On first render, if the URL carries such a decision AND we have a
  // magic token, POST the choice server-side, then scrub the query param
  // so a browser refresh doesn't re-trigger the POST.
  useEffect(() => {
    if (!order || !magicToken) return;
    const url = new URL(window.location.href);
    const decision = url.searchParams.get("decision");
    if (!decision) return;
    if (decision !== "partial" && decision !== "wait") return;
    (async () => {
      try {
        const r = await axios.post(`${API}/orders/${public_id}/partial-preference`,
          { magic_token: magicToken, choice: decision });
        setOrder({ ...order, partial_preference: r.data.partial_preference });
        toast.success("Your choice has been saved.");
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Could not save your choice.");
      } finally {
        url.searchParams.delete("decision");
        window.history.replaceState({}, "", url.toString());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.public_id]);

  // Phase 1a — Live status refresh. Automatically requests the latest
  // order state every 7s (fast enough for the customer to see "Payment
  // Submitted → Payment Verified → Preparing → Ready" transitions without
  // manual refresh, gentle enough to avoid needless load). Stops polling
  // once the order reaches a stable final state; pauses when the tab is
  // hidden. Handles network failures by simply retrying on the next tick.
  useEffect(() => {
    if (loading || error) return;
    // Terminal states — no further transitions possible, stop polling.
    const STABLE = new Set(["completed", "picked_up", "shipped", "cancelled"]);
    if (order && STABLE.has(order.status)) return;
    let intervalId = null;
    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(() => { load().catch(() => {}); }, 7000);
    };
    const stop = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    const onVis = () => (document.hidden ? stop() : start());
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [loading, error, order?.status]);

  if (loading) {
    return (
      <div className="container-gm py-24 text-center" data-testid="tracker-loading">
        <Loader2 className="animate-spin mx-auto text-[var(--panini-red)]" size={32} />
        <p className="mt-4 text-[var(--smoke)]">Loading your order…</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container-gm py-16 max-w-2xl" data-testid="tracker-error">
        <SEO title="Order · Not found" />
        <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-8 text-center">
          <AlertTriangle className="mx-auto text-[var(--panini-red)]" size={40} />
          <h1 className="font-serif text-3xl mt-4">We can&apos;t find that order.</h1>
          <p className="text-[var(--smoke)] mt-3 text-sm">{error || "The link may be incomplete. Make sure the full URL from your confirmation is used."}</p>
          <Link to="/" className="btn-yellow mt-6 inline-block" data-testid="tracker-error-home">Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <OrderTrackerBody
      order={order}
      setOrder={setOrder}
      instructions={instructions}
      magicToken={magicToken}
      publicId={public_id}
    />
  );
}

function OrderTrackerBody({ order, setOrder, instructions, magicToken, publicId }) {
  const countdown = useCountdown(order.quote_expires_at);
  const isAwaiting = order.status === "awaiting_payment";
  const expired = isAwaiting && (order.quote_expired || countdown.expired);
  const snap = order.pricing_snapshot || {};
  const lines = snap.lines || order.requested_items || [];
  const totals = snap.totals || {};
  const summary = order.fulfillment_summary || {};
  const isCompleted = order.status === "completed";
  const isCancelled = order.status === "cancelled";

  return (
    <div className="container-gm py-10 md:py-14 max-w-5xl" data-testid="order-tracker">
      <SEO title={`Order ${publicId}`} description={`Payment page for order ${publicId}.`} />

      {/* Phase 1d §10 — Redesigned customer header: order # and customer
          name at nearly the same visual weight so the customer instantly
          recognizes their own order. */}
      <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 md:p-6 shadow-[5px_5px_0_var(--panini-yellow)]">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="min-w-0">
            <div className="kicker kicker-red">Missing Sticker Order</div>
            <h1 className="font-display text-3xl md:text-5xl mt-2 tracking-tight" data-testid="order-public-id">
              ORDER #{publicId}
            </h1>
            {(() => {
              const c = order.customer || {};
              // Missing-Sticker Ordering — prefer explicit first/last
              // fields when they exist so recently-fixed orders render
              // the full name even before their legacy `name` field
              // catches up.
              const fl = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
              const display = fl || c.name;
              return display ? (
                <div className="mt-2 font-display text-2xl md:text-4xl font-bold" data-testid="tracker-customer-name">
                  {display}
                </div>
              ) : null;
            })()}
            <p className="text-[var(--smoke)] text-sm mt-3">
              Bookmark this page — it always shows the latest status.
            </p>
          </div>
          <div className="flex flex-col items-start md:items-end gap-2 shrink-0">
            <StatusPill status={order.status} />
            {order.priority && order.priority !== "normal" && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
                <Sparkles size={12} /> Priority · {order.priority.replace("_", " ")}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Phase 1d §12 — HERO banners at the top for ready/shipped states so
          the customer sees the outcome without scrolling. */}
      {order.status === "ready" && (
        <ReadyHeroBanner order={order} />
      )}
      {order.status === "shipped" && (
        <ShippedHeroBanner order={order} />
      )}

      {/* Iter E — Customer progress stepper (always visible from payment_verified onward) */}
      <ProgressStepper order={order} />

      {/* Cancelled / Completed banners */}
      {isCancelled && (
        <div data-testid="order-cancelled-banner" className="mt-6 p-5 border-2 border-red-900 bg-red-50 rounded-2xl text-red-900">
          <p className="font-bold">This order has been cancelled.</p>
          <p className="text-sm mt-1">If this is unexpected, please reach out via one of the contact channels below.</p>
        </div>
      )}
      {isCompleted && (
        <div data-testid="order-completed-banner" className="mt-6 p-5 border-2 border-emerald-900 bg-emerald-50 rounded-2xl text-emerald-900">
          <p className="font-bold">Your order has been completed. Enjoy the stickers.</p>
        </div>
      )}

      {/* PF4 · Derived fulfillment display + per-shipment history */}
      {order.fulfillment_display && (
        <FulfillmentProgressCard
          display={order.fulfillment_display}
          fulfillments={order.fulfillments || []}
        />
      )}

      {/* Phase 1b §7 — dynamic status message. Suppressed once we render
          the big Phase 1d FulfillmentPanel with its own Ready/Pending
          breakdown; only shown for edge states outside preparing/ready. */}
      {order.status_message && !isCompleted && !isCancelled &&
       order.status !== "preparing" && order.status !== "ready" && (
        <div className="mt-6 p-5 border-2 border-[var(--ink)] rounded-2xl bg-white shadow-[6px_6px_0_var(--ink)]" data-testid="dynamic-status-message">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">Fulfillment update</div>
          <p className="mt-2 text-sm text-[var(--ink)] whitespace-pre-wrap">{order.status_message}</p>
        </div>
      )}

      {/* Phase 1d — old small PartialPreferenceCard removed; the choice UI
          now lives inside the big FulfillmentPanel below. */}

      {/* Phase 1b §12 — Order details block */}
      <OrderDetailsBlock
        order={order}
        publicId={publicId}
        magicToken={magicToken}
        onOrderChanged={(fresh) => fresh && setOrder(fresh)}
      />

      {/* Pinned instructions */}
      {order.customer?.pinned_instructions && (
        <div className="mt-6 p-5 border-2 border-[var(--ink)] rounded-2xl bg-[var(--panini-yellow-soft)]" data-testid="pinned-instructions">
          <div className="text-[10px] uppercase tracking-widest font-bold">Note from you</div>
          <p className="mt-1 text-sm text-[var(--ink)] whitespace-pre-wrap">{order.customer.pinned_instructions}</p>
        </div>
      )}

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {/* Left column — Payment card (dominant) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Iter J P1 — visual pipeline (always visible) */}
          <OrderTimeline order={order} />

          {isAwaiting && (
            <PaymentPanel
              order={order}
              setOrder={setOrder}
              instructions={instructions}
              magicToken={magicToken}
              publicId={publicId}
              expired={expired}
              countdownLabel={countdown.label}
            />
          )}

          {order.status === "payment_submitted" && (
            <div className="p-6 md:p-8 border-4 border-[var(--panini-red)] rounded-2xl bg-[var(--panini-yellow-soft)] shadow-[6px_6px_0_var(--panini-red)]" data-testid="payment-submitted-panel">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-[var(--panini-red)] mt-1 shrink-0" size={28} />
                <div>
                  <div className="kicker kicker-red">Payment Submitted</div>
                  <h2 className="font-display text-2xl md:text-3xl mt-1">Thank you!</h2>
                  <p className="text-sm md:text-base mt-2 max-w-xl">
                    We&apos;ve received your payment confirmation.
                  </p>

                  <div className="mt-5 p-4 rounded-xl border-2 border-[var(--ink)] bg-white">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Current Status</div>
                    <div className="font-display text-xl mt-1 flex items-center gap-2" data-testid="payment-submitted-status">
                      <Loader2 className="animate-spin text-[var(--panini-red)]" size={18} />
                      Awaiting Payment Confirmation
                    </div>
                  </div>

                  <p className="text-sm text-[var(--smoke)] mt-4 leading-relaxed max-w-xl">
                    We&apos;ll verify your payment. Once approved, your order status will
                    automatically become <b className="text-[var(--ink)]">Payment Confirmed</b>
                    &nbsp;and then <b className="text-[var(--ink)]">Preparing Order</b>. Feel free
                    to refresh this page anytime — you&apos;ll always see the latest status.
                  </p>

                  {order.payment && (
                    <div className="mt-4 text-sm border-t border-[var(--stone)] pt-4 space-y-1">
                      <div><span className="text-[var(--smoke)]">Sent from:</span> <b>{order.payment.zelle_name}</b></div>
                      <div><span className="text-[var(--smoke)]">Amount:</span> <b>{usdFmt(order.payment.amount_claimed)}</b></div>
                      {order.payment.paid_at_claimed && <div><span className="text-[var(--smoke)]">Paid at:</span> {new Date(order.payment.paid_at_claimed).toLocaleString()}</div>}
                    </div>
                  )}

                  {/* Phase 1c — customer WhatsApp CTA. Only while awaiting
                      admin verification; disappears automatically the
                      moment status transitions to payment_verified. */}
                  <WhatsAppVerifyButton orderNumber={order.public_id} />
                </div>
              </div>
            </div>
          )}

          {(order.status === "payment_verified" || order.status === "preparing") && (
            <div className="p-6 border-4 border-emerald-600 rounded-2xl bg-emerald-50" data-testid="preparing-panel">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-emerald-600 mt-1 shrink-0" size={26} />
                <div>
                  <div className="kicker kicker-red">Payment Confirmed!</div>
                  <h2 className="font-display text-2xl md:text-3xl mt-1">We have verified your payment.</h2>
                  <p className="text-sm mt-2">Your stickers are now being prepared.</p>
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-emerald-700 bg-white px-3 py-1 text-sm font-bold">
                    Current Status: <span className="text-emerald-700">Preparing Order</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {order.status === "ready" && (
            <div className="p-6 border-4 border-emerald-600 rounded-2xl bg-emerald-50" data-testid="ready-panel">
              <div className="kicker kicker-red">Ready for Pickup</div>
              <h2 className="font-display text-2xl md:text-3xl mt-1">Your order is ready for pickup!</h2>
              <div className="mt-4 p-4 rounded-xl border-2 border-[var(--ink)] bg-white" data-testid="pickup-address">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Pickup Location</div>
                <div className="mt-1 font-display text-lg">Your Phone Plug</div>
                <div className="text-sm">20719 NW 2nd Ave<br />Miami, FL 33160</div>
              </div>
            </div>
          )}

          {order.status === "shipped" && (() => {
            const ship = order.shipment || {};
            const carrier = ship.carrier || (order.delivery?.code === "usps_stamp" ? "USPS Stamp" : "UPS Ground");
            const isUsps = String(carrier).toLowerCase().includes("usps");
            const shipDate = ship.shipment_date || order.shipped_at;
            return (
              <div className="p-6 border-4 border-emerald-600 rounded-2xl bg-emerald-50" data-testid="shipped-panel">
                <div className="kicker kicker-red">Shipped</div>
                <h2 className="font-display text-2xl md:text-3xl mt-1">Your order has shipped!</h2>
                <div className="mt-4 p-4 rounded-xl border-2 border-[var(--ink)] bg-white space-y-2" data-testid="shipment-details">
                  <div className="text-sm">
                    <span className="font-bold" data-testid="shipment-carrier">Shipped by {carrier}</span>
                    {shipDate && (
                      <> · <span className="text-[var(--smoke)]" data-testid="shipment-date">
                        {new Date(shipDate).toLocaleDateString()}
                      </span></>
                    )}
                  </div>
                  {isUsps ? (
                    <>
                      <div className="text-sm text-[var(--smoke)]" data-testid="shipment-no-tracking">
                        No tracking number — USPS Stamp mail is untracked.
                      </div>
                      <div className="text-sm text-[var(--smoke)]">
                        Estimated delivery: <b className="text-[var(--ink)]">2–7 business days</b> after shipment.
                      </div>
                    </>
                  ) : ship.tracking_number ? (
                    <>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">UPS Tracking Number</div>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="font-mono font-bold text-lg" data-testid="shipment-tracking">{ship.tracking_number}</span>
                          <a
                            href={`https://www.ups.com/track?tracknum=${encodeURIComponent(ship.tracking_number)}`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-xs underline text-[var(--panini-red)] font-bold"
                            data-testid="shipment-tracking-link"
                          >Track on UPS ↗</a>
                        </div>
                      </div>
                      <div className="text-sm text-[var(--smoke)]">
                        Estimated delivery: <b className="text-[var(--ink)]">1–2 business days</b> from ship date.
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            );
          })()}

          {order.status === "completed" && (
            <div className="p-6 border-4 border-emerald-600 rounded-2xl bg-emerald-50" data-testid="completed-panel">
              <div className="kicker kicker-red">Order Completed</div>
              <h2 className="font-display text-2xl md:text-3xl mt-1">Thank you for ordering with CollectorUp!</h2>
              <p className="text-sm mt-2 text-[var(--smoke)]">
                Enjoy your stickers. If anything is missing or damaged, reply to any of our order emails within 7 days and we&apos;ll make it right.
              </p>
            </div>
          )}

          {/* Guest → Account conversion CTA (visible whenever order is unlinked) */}
          <GuestConversionCard order={order} publicId={publicId} magicToken={magicToken} setOrder={setOrder} />

          {(order.status === "payment_verified" || order.status === "preparing" || order.status === "ready") && (
            <FulfillmentPanel order={order} summary={summary} publicId={publicId}
                              magicToken={magicToken} setOrder={setOrder} />
          )}

          {/* Requested Items */}
          <RequestedItemsCard lines={lines} totals={totals} snap={snap} />
        </div>

        {/* Right column — sidebar */}
        <div className="space-y-6">
          <SupportCard instructions={instructions} />
          <OrderMetaCard order={order} publicId={publicId} magicToken={magicToken} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Payment panel — awaiting_payment
// ─────────────────────────────────────────────────────────────────────
function PaymentPanel({ order, setOrder, instructions, magicToken, publicId, expired, countdownLabel }) {
  const totals = order.pricing_snapshot?.totals || {};
  const total = totals.total || 0;
  const [busy, setBusy] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [zelleName, setZelleName] = useState("");
  const [amount, setAmount] = useState(total.toFixed(2));
  const [memo, setMemo] = useState(`Order ${publicId}`);
  const [refreshing, setRefreshing] = useState(false);
  const [shopifyBusy, setShopifyBusy] = useState(false);
  const reusingUrl = false;

  const handleShopifyCheckout = async () => {
    setShopifyBusy(true);
    try {
      const r = await axios.post(`${API}/orders/${publicId}/shopify-checkout`, {
        magic_token: magicToken,
      });
      const url = r.data?.invoice_url;
      if (!url) {
        // Backend returned 200 but no invoice link — surface a real message
        // instead of the generic "Something went wrong" fallback so the
        // customer knows their next step (retry or use Zelle below).
        toast.error(
          "Shopify did not return a checkout link. Please try again in a moment, or use Zelle below."
        );
        setShopifyBusy(false);
        return;
      }
      // Anonymous conversion tracking — no amounts, no PII.
      track(EVENT.PAYMENT_SENT_CLICKED, {
        delivery_method: order?.delivery?.code || "unknown",
        payment_method:  "shopify",
      }, { once: true, key: `paysent-shopify-${publicId}` });
      window.location.assign(url);
    } catch (e) {
      // Chain the fallbacks EXPLICITLY. `formatApiError(undefined)` returns
      // the string "Something went wrong. Please try again." — which is
      // truthy — so a naïve `formatApiError(detail) || e?.message` chain
      // would silently swallow every non-JSON error (Cloudflare HTML pages,
      // timeouts, aborted requests, network drops on mobile). We MUST
      // detect those cases and emit a specific, action-oriented message.
      const rawDetail = e?.response?.data?.detail;
      const httpStatus = e?.response?.status;
      let msg;
      if (rawDetail != null) {
        msg = formatApiError(rawDetail);
      } else if (httpStatus && httpStatus >= 500) {
        // Cloudflare / proxy / origin 5xx with an HTML body (or no body).
        msg = "Card checkout is temporarily unavailable. Please try again in a moment, or use Zelle below.";
      } else if (e?.code === "ECONNABORTED" || e?.code === "ERR_NETWORK") {
        msg = "Network hiccup while opening Shopify checkout. Please check your connection and try again, or use Zelle below.";
      } else if (e?.message && e.message !== "Network Error") {
        msg = `Could not open Shopify checkout: ${e.message}. Please try again or use Zelle below.`;
      } else {
        msg = "Could not open Shopify checkout. Please try again in a moment, or use Zelle below.";
      }
      toast.error(msg);
      setShopifyBusy(false);
    }
  };

  const [zelleExpanded, setZelleExpanded] = useState(false);

  const scrollToZelle = () => {
    setZelleExpanded(true);
    // wait a tick for the DOM to expand before scrolling.
    requestAnimationFrame(() => {
      const el = document.getElementById("zelle-instructions");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/orders/${publicId}/refresh-quote`, { magic_token: magicToken });
      // Reload full order so downstream renders new totals
      const fresh = await axios.get(`${API}/orders/${publicId}`, { params: { t: magicToken } });
      setOrder(fresh.data);
      toast.success("Quote refreshed with the latest pricing.");
      const newTotal = r.data?.pricing_snapshot?.totals?.total || 0;
      setAmount(newTotal.toFixed(2));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not refresh the quote.");
    } finally {
      setRefreshing(false);
    }
  };

  const doMarkPaid = async () => {
    if (!zelleName.trim()) return toast.error("Please enter the Zelle name you paid from.");
    if (!Number(amount) || Number(amount) <= 0) return toast.error("Please confirm the amount you sent.");
    setBusy(true);
    try {
      await axios.post(`${API}/orders/${publicId}/mark-paid`, {
        magic_token: magicToken,
        zelle_name: zelleName.trim(),
        amount_claimed: Number(amount),
        paid_at_claimed: new Date().toISOString(),
        memo: memo.trim() || undefined,
      });
      // Anonymous conversion — no amount, memo, name, or reference is
      // transmitted (the shared PII scrub would drop them anyway).
      track(EVENT.PAYMENT_SENT_CLICKED, {
        delivery_method: order?.delivery?.code || "unknown",
        payment_method:  "zelle",
      }, { once: true, key: `paysent-${publicId}` });
      const fresh = await axios.get(`${API}/orders/${publicId}`, { params: { t: magicToken } });
      setOrder(fresh.data);
      toast.success("Thanks! We've received your payment claim.");
      setShowConfirm(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not submit your payment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-8 border-2 border-[var(--ink)] rounded-2xl bg-white shadow-[6px_6px_0_var(--ink)]" data-testid="payment-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="kicker kicker-red">Your order is almost complete!</div>
          <h2 className="font-display text-2xl md:text-3xl mt-2" data-testid="payment-headline">Choose how you want to pay.</h2>
          <p className="text-sm text-[var(--smoke)] mt-2 max-w-md">
            To confirm your order, send your Zelle payment using the details below. Include your Order ID in the payment description.
          </p>
        </div>
        <div
          className={`text-right ${expired ? "text-[var(--panini-red)]" : "text-[var(--ink)]"}`}
          data-testid="quote-countdown"
        >
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">
            {expired ? "Quote expired" : "Quote holds for"}
          </div>
          <div className="text-2xl font-mono font-bold flex items-center gap-2 justify-end">
            <Clock size={18} />
            {expired ? "00:00:00" : countdownLabel}
          </div>
        </div>
      </div>

      {expired ? (
        <div className="mt-6 p-5 border-2 border-[var(--panini-red)] bg-red-50 rounded-xl" data-testid="quote-expired-block">
          <p className="font-bold text-[var(--panini-red)] flex items-center gap-2"><AlertTriangle size={16}/> Your quote has expired.</p>
          <p className="text-sm mt-2 text-[var(--ink)]">Prices can shift; please refresh to get an up-to-date quote before you pay.</p>
          <button
            type="button"
            onClick={doRefresh}
            disabled={refreshing}
            data-testid="refresh-quote-btn"
            className="btn-yellow mt-4"
          >
            {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Refresh quote
          </button>
        </div>
      ) : (
        <>
          {/* ── Payment choice · top of page · mobile-first ─────────── */}
          <div
            className="mt-6 p-5 rounded-2xl border-2 border-[var(--ink)] bg-[var(--paper)]"
            data-testid="payment-choice"
          >
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
              Choose how you want to pay
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <button
                  type="button"
                  onClick={handleShopifyCheckout}
                  disabled={shopifyBusy}
                  data-testid="payment-choice-card-btn"
                  className="btn-red w-full justify-center text-base py-3"
                >
                  {shopifyBusy
                    ? <><Loader2 size={18} className="animate-spin" /> Opening secure checkout…</>
                    : <><ShieldCheck size={18} /> Pay by Card / Apple Pay / Google Pay</>}
                </button>
                <p className="mt-1.5 text-[11px] text-[var(--smoke)]" data-testid="payment-choice-card-note">
                  Secure checkout powered by Shopify.
                </p>
                <p className="mt-0.5 text-[10px] text-[var(--smoke)] italic" data-testid="payment-choice-card-availability">
                  Available payment methods depend on your device and Shopify checkout.
                </p>
              </div>
              <div>
                <button
                  type="button"
                  onClick={scrollToZelle}
                  data-testid="payment-choice-zelle-btn"
                  aria-expanded={zelleExpanded}
                  aria-controls="zelle-instructions"
                  className="btn-ghost border-2 border-[var(--ink)] w-full justify-center text-base py-3"
                >
                  <Banknote size={18} /> Pay with Zelle
                  <ChevronDown
                    size={16}
                    className={`ml-1 transition-transform ${zelleExpanded ? "rotate-180" : ""}`}
                    aria-hidden="true"
                  />
                </button>
                <p className="mt-1.5 text-[11px] text-[var(--smoke)]" data-testid="payment-choice-zelle-note">
                  Instant bank transfer.
                </p>
              </div>
            </div>
          </div>

          {/* ── Zelle section · collapsed by default · expanded when the
              Zelle CTA is tapped. Contains recipient details, order-ID
              chip, 6-step instructions, and the "I've Sent Payment"
              confirmation control — all in one place so the mobile
              customer isn't asked to scroll past a wall of Zelle content
              when they wanted to pay by card. */}
          <div
            id="zelle-instructions"
            className="mt-6 scroll-mt-24"
            data-testid="zelle-section"
            data-expanded={zelleExpanded ? "true" : "false"}
          >
            {!zelleExpanded && (
              <button
                type="button"
                onClick={() => setZelleExpanded(true)}
                data-testid="zelle-expand-toggle"
                aria-expanded="false"
                aria-controls="zelle-collapsible-body"
                className="w-full flex items-center justify-between gap-3 p-4 rounded-xl border-2 border-dashed border-[var(--ink)] bg-white text-left"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Zelle instructions</div>
                  <div className="text-sm font-bold mt-0.5">Show me how to pay with Zelle</div>
                </div>
                <ChevronDown size={20} aria-hidden="true" />
              </button>
            )}

            {zelleExpanded && (
              <div id="zelle-collapsible-body" data-testid="zelle-collapsible-body" className="space-y-5">
                {/* Amount + Zelle recipient block */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-5 rounded-2xl border-4 border-[var(--panini-red)] bg-[var(--panini-yellow-soft)]" data-testid="pay-amount-card">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Amount Due</div>
                    <div className="font-display text-4xl md:text-5xl text-[var(--panini-red)] mt-1 tabular-nums" data-testid="pay-amount">
                      {usdFmt(total)}
                    </div>
                  </div>
                  <div className="p-5 rounded-2xl border-4 border-[var(--ink)] bg-white space-y-2" data-testid="pay-zelle-card">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Zelle Number</div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="font-mono font-bold text-xl" data-testid="pay-zelle-number">786-252-8059</div>
                        <CopyChip value="7862528059" label="Zelle number" testid="pay-zelle-number-copy" />
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Recipient Name</div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="font-bold text-base" data-testid="pay-recipient">Unlocking Wireless</div>
                        <CopyChip value="Unlocking Wireless" label="Recipient" testid="pay-recipient-copy" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order ID display */}
                <div className="p-4 rounded-xl border-2 border-[var(--ink)] bg-[var(--paper)] flex items-center justify-between gap-3 flex-wrap" data-testid="pay-order-id-card">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Order ID · include this in the Zelle memo</div>
                    <div className="font-mono font-bold text-lg md:text-xl mt-1" data-testid="pay-order-id">{publicId}</div>
                  </div>
                  <CopyChip value={publicId} label="Order ID" testid="pay-order-id-copy" />
                </div>

                {/* Instructions */}
                <div className="p-5 rounded-xl border-2 border-[var(--ink)] bg-white" data-testid="pay-instructions">
                  <div className="text-[10px] uppercase tracking-widest font-bold">Send payment in 6 steps</div>
                  <ol className="mt-2 text-sm space-y-1.5 list-decimal list-inside">
                    <li>Open Zelle in your banking app.</li>
                    <li>Send payment of <b>{usdFmt(total)}</b>.</li>
                    <li>Confirm the recipient shows as <b>Unlocking Wireless</b>.</li>
                    <li>Paste your Order ID <b>{publicId}</b> into the memo.</li>
                    <li>Complete the transfer.</li>
                    <li>Return here and click <b>I&apos;ve Sent Payment</b>.</li>
                  </ol>
                </div>

                {/* Zelle confirmation CTA — lives inside the expanded Zelle section */}
                <div className="space-y-3" data-testid="pay-cta-stack">
                  <button
                    type="button"
                    onClick={() => setShowConfirm(true)}
                    data-testid="ive-paid-btn"
                    className="btn-ghost border-2 border-[var(--ink)] w-full justify-center text-base py-3"
                  >
                    <CheckCircle2 size={20} /> I&apos;ve Sent Payment via Zelle
                  </button>
                  <p className="text-[11px] text-[var(--smoke)] text-center" data-testid="pay-quote-note">
                    Total <b>{usdFmt(total)}</b> matches your quote exactly.
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" data-testid="mark-paid-modal">
          <div className="w-full max-w-lg bg-white border-2 border-[var(--ink)] rounded-2xl shadow-[8px_8px_0_var(--ink)] p-6">
            <div className="flex items-start justify-between">
              <h3 className="font-display text-2xl">Confirm your Zelle payment</h3>
              <button className="text-sm underline" onClick={() => setShowConfirm(false)} data-testid="mark-paid-cancel">Cancel</button>
            </div>
            <p className="text-sm text-[var(--smoke)] mt-2">Please only mark as paid once your Zelle transfer has actually been sent. We&apos;ll match your details against our incoming transfers.</p>

            <div className="mt-4 space-y-3">
              <Field label="Full name on your Zelle account" testid="mp-zelle-name">
                <input
                  className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
                  value={zelleName} onChange={(e) => setZelleName(e.target.value)} maxLength={200}
                  data-testid="mp-zelle-name-input"
                />
              </Field>
              <Field label="Amount you sent (USD)" testid="mp-amount">
                <input
                  type="number" step="0.01" min="0"
                  className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  data-testid="mp-amount-input"
                />
              </Field>
              <Field label="Reference / memo (optional)" testid="mp-memo">
                <input
                  className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
                  value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={200}
                  data-testid="mp-memo-input"
                />
              </Field>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={doMarkPaid}
                disabled={busy}
                data-testid="mp-confirm-btn"
                className="btn-red"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Confirm payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ZelleField({ icon, label, value, testid, hint }) {
  return (
    <div className="border-2 border-[var(--ink)] rounded-lg p-3 bg-[var(--paper)]">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1">{icon}{label}</div>
        {value ? <CopyChip value={value} label={label} testid={`${testid}-copy`} /> : null}
      </div>
      <div className="mt-1 font-mono text-sm break-all" data-testid={testid}>{value || <span className="opacity-40">— not set —</span>}</div>
      {hint && <div className="mt-1 text-[11px] text-[var(--smoke)] italic">{hint}</div>}
    </div>
  );
}

// Iter J — QR code removed per spec. Customers enter the Zelle number
// directly in their bank's Zelle interface.

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-xs uppercase tracking-widest font-bold mb-1">{label}</span>
      {children}
    </label>
  );
}

function RequestedItemsCard({ lines, totals, snap }) {
  return (
    <div className="p-6 border-2 border-[var(--ink)] rounded-2xl bg-white" data-testid="requested-items-card">
      <h3 className="font-display text-2xl">Your quote breakdown</h3>
      <p className="text-xs text-[var(--smoke)] mt-1">
        Prices are frozen at the time you submitted — we honor this quote for the entire hold window.
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest border-b-2 border-[var(--ink)]">
              <th className="text-left py-2">Code</th>
              <th className="text-left py-2">Type</th>
              <th className="text-right py-2">Qty</th>
              <th className="text-right py-2">Unit</th>
              <th className="text-right py-2">Line</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={`${l.code}-${i}`} className="border-b border-[var(--stone)]">
                <td className="py-2 font-mono font-bold" data-testid={`line-code-${l.code}`}>{l.code}</td>
                <td className="py-2 text-xs text-[var(--smoke)]">{l.matched_rule_label || "—"}</td>
                <td className="py-2 text-right">{l.qty}</td>
                <td className="py-2 text-right">{usdFmt(l.unit_price)}</td>
                <td className="py-2 text-right font-bold">{usdFmt(l.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <TotalCell label="Stickers subtotal" value={totals.subtotal} />
        {totals.small_order_fee > 0 && <TotalCell label="Small-order fee" value={totals.small_order_fee} />}
        {totals.service_fee > 0 && <TotalCell label="Service fee" value={totals.service_fee} />}
        {totals.shipping > 0 && <TotalCell label="Shipping" value={totals.shipping} />}
        {totals.tax > 0 && <TotalCell label="Tax" value={totals.tax} />}
        <TotalCell label="Total" value={totals.total} bold />
      </div>
      {snap?.flags?.excluded_from_pricing?.length > 0 && (
        <div className="mt-4 text-xs text-[var(--smoke)]" data-testid="excluded-items">
          Excluded from pricing: <span className="font-mono">{snap.flags.excluded_from_pricing.join(", ")}</span>
        </div>
      )}
    </div>
  );
}

function TotalCell({ label, value, bold }) {
  return (
    <div className={`p-3 rounded-lg border-2 border-[var(--ink)] ${bold ? "bg-[var(--panini-yellow)]" : "bg-white"}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold">{label}</div>
      <div className={`mt-1 ${bold ? "font-display text-2xl" : "font-mono text-base font-bold"}`}>{usdFmt(value)}</div>
    </div>
  );
}

function FulfillmentPanel({ order, summary, publicId, magicToken, setOrder }) {
  const pct = Math.round(summary?.percent_complete ?? 0);
  const ready = Number(summary?.ready_total ?? 0);
  const pending = Number(summary?.pending_total ?? 0);
  const requested = Number(summary?.requested_total ?? 0);
  const removed = Number(summary?.removed_total ?? 0);
  const items = order.fulfillment_items || [];
  const readyItems = items.filter((it) => Number(it.qty_picked || 0) > 0 && it.status !== "Removed");
  const pendingItems = items.filter((it) => (it.status === "Pending" || it.status === "PartiallyPicked")
                                             && Number(it.qty_remaining || 0) > 0);
  const isPickup = (order.delivery?.code || "").toLowerCase() === "local_pickup";
  const currentChoice = order.partial_preference?.choice || "";

  const setChoice = async (choice) => {
    try {
      const r = await axios.post(`${API}/orders/${publicId}/partial-preference`,
        { magic_token: magicToken, choice });
      setOrder({ ...order, partial_preference: r.data.partial_preference });
      toast.success("Your choice has been recorded.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not save your choice.");
    }
  };

  return (
    <div className="p-6 md:p-8 border-4 border-emerald-900 bg-emerald-50 rounded-2xl shadow-[6px_6px_0_var(--ink)]"
         data-testid="fulfillment-panel">
      <div className="kicker kicker-red">Fulfillment progress</div>
      <h3 className="font-display text-3xl md:text-4xl text-emerald-900 mt-2">
        {ready} of {requested} stickers ready
      </h3>

      {/* Big progress bar */}
      <div className="mt-5">
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-900/80">Progress</span>
          <span className="font-display text-4xl md:text-5xl font-black text-emerald-900" data-testid="progress-percent">{pct}%</span>
        </div>
        <div className="mt-2 h-6 bg-white border-2 border-emerald-900 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-600 transition-all duration-500" style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      </div>

      {/* Large metric tiles */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fulfillment-metrics">
        <MetricCellLarge label="Requested" value={requested} tone="gray" />
        <MetricCellLarge label="Ready" value={ready} tone="green" />
        <MetricCellLarge label="Pending" value={pending} tone="yellow" />
        <MetricCellLarge label="Removed" value={removed} tone="red" />
      </div>

      {/* Ready + Pending explicit lists */}
      {(readyItems.length > 0 || pendingItems.length > 0) && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <StickerList
            heading="READY"
            emptyText="None picked yet."
            emoji="✅"
            items={readyItems}
            testid="ready-list"
          />
          <StickerList
            heading="PENDING"
            emptyText="All stickers are ready."
            emoji="⏳"
            items={pendingItems}
            testid="pending-list"
          />
        </div>
      )}

      {/* Big customer action buttons — only while there's still something
          to decide (partial delivery is meaningful only when ready>0 AND
          pending>0 and the order isn't already terminal). */}
      {pending > 0 && ready > 0 && (order.status === "preparing" || order.status === "ready") && (
        <div className="mt-8" data-testid="partial-actions">
          <div className="text-xs uppercase tracking-widest font-bold text-emerald-900/80 mb-3">
            How would you like to proceed?
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setChoice("partial")}
              className={
                "px-6 py-4 rounded-2xl border-4 text-left font-display text-lg md:text-xl transition " +
                (currentChoice === "partial"
                  ? "bg-emerald-600 text-white border-emerald-900 shadow-[4px_4px_0_var(--ink)]"
                  : "bg-white border-emerald-900 hover:bg-emerald-100 text-emerald-900")
              }
              data-testid={isPickup ? "btn-partial-pickup" : "btn-partial-ship"}
            >
              {isPickup ? "PICK UP AVAILABLE STICKERS NOW" : "SHIP AVAILABLE STICKERS NOW"}
              {!isPickup && (
                <div className="text-xs font-normal mt-2 opacity-80">
                  Additional shipping charges may apply if remainder ships later.
                </div>
              )}
            </button>
            <button
              type="button"
              onClick={() => setChoice("wait")}
              className={
                "px-6 py-4 rounded-2xl border-4 text-left font-display text-lg md:text-xl transition " +
                (currentChoice === "wait"
                  ? "bg-[var(--ink)] text-white border-[var(--ink)] shadow-[4px_4px_0_var(--panini-yellow)]"
                  : "bg-white border-[var(--ink)] hover:bg-neutral-100 text-[var(--ink)]")
              }
              data-testid="btn-wait-complete"
            >
              WAIT FOR COMPLETE ORDER
              <div className="text-xs font-normal mt-2 opacity-80">
                We&apos;ll notify you when every sticker is in.
              </div>
            </button>
          </div>
          {currentChoice && (
            <div className="mt-3 text-xs text-emerald-900/80" data-testid="partial-choice-echo">
              Current choice: <b>{currentChoice === "wait" ? "Wait for complete order"
                                    : isPickup ? "Pick up available now" : "Ship available now"}</b>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StickerList({ heading, emptyText, emoji, items, testid }) {
  return (
    <div className="p-4 rounded-xl border-2 border-emerald-900 bg-white" data-testid={testid}>
      <div className="font-display text-lg text-emerald-900">{heading}</div>
      {items.length === 0 ? (
        <div className="text-sm text-[var(--smoke)] mt-2">{emptyText}</div>
      ) : (
        <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
          {items.map((it) => {
            const label = it.display_name || it.player_name || it.subject;
            return (
              <li key={it.code} className="text-sm font-mono" data-testid={`${testid}-${it.code}`}>
                {emoji} {it.code}
                {label && (
                  <span className="font-sans text-xs text-[var(--smoke)] ml-1" data-testid={`${testid}-name-${it.code}`}>
                    · {label}
                  </span>
                )}
                {Number(it.qty_requested || 0) > 1 && Number(it.qty_picked || 0) !== Number(it.qty_requested || 0) && (
                  <span className="text-[10px] text-[var(--smoke)] ml-1">
                    ({it.qty_picked}/{it.qty_requested})
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function MetricCellLarge({ label, value, tone }) {
  const cls = {
    green: "bg-emerald-100 border-emerald-900 text-emerald-900",
    yellow: "bg-yellow-100 border-yellow-900 text-yellow-900",
    red: "bg-red-100 border-red-900 text-red-900",
    gray: "bg-white border-[var(--ink)] text-[var(--ink)]",
  }[tone] || "bg-white border-[var(--ink)] text-[var(--ink)]";
  return (
    <div className={"p-4 rounded-xl border-2 " + cls}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="font-display text-3xl md:text-4xl font-black mt-1">{value}</div>
    </div>
  );
}

function ReadyHeroBanner({ order }) {
  const isPickup = (order.delivery?.code || "").toLowerCase() === "local_pickup";
  return (
    <div
      className="mt-6 rounded-2xl border-4 border-emerald-900 bg-emerald-500 p-6 md:p-8 text-white shadow-[8px_8px_0_var(--ink)]"
      data-testid="ready-hero-banner"
    >
      <div className="flex items-start gap-4">
        <CheckCircle2 className="shrink-0" size={48} />
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">
            {isPickup ? "Ready for Pickup" : "Ready to Ship"}
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-black mt-1 tracking-tight">
            ✅ Your order is {isPickup ? "ready for pickup" : "ready to ship"}
          </h2>
        </div>
      </div>
    </div>
  );
}

function ShippedHeroBanner({ order }) {
  return (
    <div
      className="mt-6 rounded-2xl border-4 border-emerald-900 bg-emerald-500 p-6 md:p-8 text-white shadow-[8px_8px_0_var(--ink)]"
      data-testid="shipped-hero-banner"
    >
      <div className="flex items-start gap-4">
        <CheckCircle2 className="shrink-0" size={48} />
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">Shipped</div>
          <h2 className="font-display text-3xl md:text-5xl font-black mt-1 tracking-tight">
            ✅ Your order is on its way
          </h2>
          {order.shipment?.tracking_number && (
            <div className="mt-2 text-sm md:text-base">
              Tracking: <span className="font-mono font-bold">{order.shipment.tracking_number}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricCell({ label, value, tone }) {
  const cls = {
    green:  "bg-emerald-100 text-emerald-900",
    yellow: "bg-yellow-100 text-yellow-900",
    red:    "bg-red-100 text-red-900",
  }[tone] || "bg-white text-[var(--ink)]";
  return (
    <div className={`p-3 rounded-lg border-2 ${tone ? "border-current" : "border-[var(--ink)]"} ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{label}</div>
      <div className="mt-1 font-display text-2xl">{value}</div>
    </div>
  );
}

// Iter E — Customer-facing progress stepper. Never exposes internal
// fulfillment session details; only surfaces the coarse-grained public
// stages so the customer knows where their order stands.
function ProgressStepper({ order }) {
  const isPickup = (order.delivery?.code || "") === "local_pickup";
  const isCancelled = order.status === "cancelled";
  const readyLabel = isPickup ? "Ready for pickup" : "Ready for shipping";
  const stages = [
    { key: "payment_received", label: "Payment received", statuses: ["payment_verified", "preparing", "ready", "completed"] },
    { key: "preparing", label: "Preparing order", statuses: ["preparing", "ready", "completed"] },
    { key: "ready", label: readyLabel, statuses: ["ready", "completed"] },
    { key: "completed", label: "Completed", statuses: ["completed"] },
  ];
  // Phase 1d §11 — the "Currently being fulfilled" banner must ONLY show
  // while the order is actively being picked. Once the order reaches ready
  // / shipped / completed, the higher-status hero banner takes over.
  const isActive = order.is_being_fulfilled === true
      && ["payment_verified", "preparing"].includes(order.status);

  if (order.status === "awaiting_payment" || order.status === "payment_submitted") {
    return null; // Pre-payment states are handled by PaymentPanel + payment_submitted banner
  }

  return (
    <div className="mt-6" data-testid="progress-stepper">
      {isActive && !isCancelled && (
        <div className="mb-3 p-3 border-2 border-emerald-900 bg-emerald-50 rounded-xl flex items-center gap-3" data-testid="being-fulfilled-banner">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-600 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-700"></span>
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-900">Currently being fulfilled</div>
            <p className="text-sm text-emerald-900/80">A member of our team is pulling your stickers right now.</p>
          </div>
        </div>
      )}
      <ol className={`flex flex-col md:flex-row md:items-center gap-2 md:gap-0 md:justify-between ${isCancelled ? "opacity-40" : ""}`}>
        {stages.map((s, i) => {
          const done = s.statuses.includes(order.status);
          const isCurrent = !done && stages.slice(0, i).every((prev) => prev.statuses.includes(order.status));
          return (
            <React.Fragment key={s.key}>
              <li className="flex items-center gap-2 flex-1" data-testid={`stage-${s.key}-${done ? "done" : isCurrent ? "current" : "todo"}`}>
                <div
                  className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0
                    ${done ? "bg-emerald-600 border-emerald-900 text-white" : isCurrent ? "bg-[var(--panini-yellow)] border-[var(--ink)] text-[var(--ink)]" : "bg-white border-[var(--stone)] text-[var(--smoke)]"}`}
                >
                  {done ? <CheckCircle2 size={16} /> : <span className="text-xs font-bold">{i + 1}</span>}
                </div>
                <div>
                  <div className={`text-xs uppercase tracking-widest font-bold ${done ? "text-emerald-900" : isCurrent ? "text-[var(--ink)]" : "text-[var(--smoke)]"}`}>
                    {s.label}
                  </div>
                </div>
              </li>
              {i < stages.length - 1 && (
                <div className="hidden md:block flex-1 h-[2px] bg-[var(--stone)] mx-2">
                  <div className={`h-full ${done ? "bg-emerald-600" : "bg-transparent"}`} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </div>
  );
}

function SupportCard({ instructions }) {
  return (
    <div className="p-5 border-2 border-[var(--ink)] rounded-2xl bg-white" data-testid="support-card">
      <div className="text-[10px] uppercase tracking-widest font-bold">Need help?</div>
      <p className="text-sm text-[var(--smoke)] mt-1">Reach us on any channel — we usually respond immediately during business hours.</p>
      <div className="mt-3 space-y-2 text-sm">
        {instructions?.support_whatsapp_url && (
          <a target="_blank" rel="noreferrer" href={instructions.support_whatsapp_url}
             className="flex items-center justify-between border-2 border-[var(--ink)] rounded-lg px-3 py-2 hover:bg-[var(--panini-yellow-soft)]"
             data-testid="support-whatsapp">
            WhatsApp <ArrowUpRight size={14} />
          </a>
        )}
        {instructions?.support_instagram_url && (
          <a target="_blank" rel="noreferrer" href={instructions.support_instagram_url}
             className="flex items-center justify-between border-2 border-[var(--ink)] rounded-lg px-3 py-2 hover:bg-[var(--panini-yellow-soft)]"
             data-testid="support-instagram">
            Instagram DM <ArrowUpRight size={14} />
          </a>
        )}
        {instructions?.support_contact_email && (
          <a href={`mailto:${instructions.support_contact_email}`}
             className="flex items-center justify-between border-2 border-[var(--ink)] rounded-lg px-3 py-2 hover:bg-[var(--panini-yellow-soft)]"
             data-testid="support-email">
            {instructions.support_contact_email} <Mail size={14} />
          </a>
        )}
      </div>
    </div>
  );
}

function OrderMetaCard({ order, publicId, magicToken }) {
  const trackerUrl = `${window.location.origin}/orders/${publicId}?t=${magicToken}`;
  return (
    <div className="p-5 border-2 border-[var(--ink)] rounded-2xl bg-white" data-testid="meta-card">
      <div className="text-[10px] uppercase tracking-widest font-bold">Order details</div>
      <dl className="mt-3 text-sm space-y-2">
        <div className="flex items-center justify-between gap-2">
          <dt className="opacity-70">Order ID</dt>
          <dd className="font-mono font-bold">{publicId}</dd>
        </div>
        {order.album_slug && (
          <div className="flex items-center justify-between gap-2">
            <dt className="opacity-70">Album</dt>
            <dd className="text-right text-xs">{order.album_slug}</dd>
          </div>
        )}
        {order.delivery?.code && (
          <div className="flex items-center justify-between gap-2">
            <dt className="opacity-70">Delivery</dt>
            <dd className="text-right text-xs">{order.delivery.code.replace("_", " ")}</dd>
          </div>
        )}
        {order.submitted_at && (
          <div className="flex items-center justify-between gap-2">
            <dt className="opacity-70">Submitted</dt>
            <dd className="text-right text-xs">{new Date(order.submitted_at).toLocaleString()}</dd>
          </div>
        )}
      </dl>
      <button
        type="button"
        onClick={() => { navigator.clipboard.writeText(trackerUrl); toast.success("Order link copied"); }}
        className="w-full mt-4 btn-ghost text-xs"
        data-testid="copy-order-link"
      >
        <Copy size={12} /> Copy shareable order link
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Guest → CollectorUp Account conversion (Iter J)
// ─────────────────────────────────────────────────────────────────────
// Rendered on the tracker page whenever the order is not yet attached to
// a collector account AND the visitor is not already signed in. Provides
// two paths:
//   • Create a new account using the same email → auto-signs in + attaches
//     all previous guest orders sharing that email.
//   • Sign in to an existing account → links this order to that account.
// ─────────────────────────────────────────────────────────────────────
function GuestConversionCard({ order, publicId, magicToken, setOrder }) {
  const { collector, loading: authLoading, refresh: refreshAuth } = useCollectorAuth();
  const [mode, setMode] = useState("idle"); // idle | signup | login
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(order?.customer?.email || "");
  const [phone, setPhone] = useState(order?.customer?.phone || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Wait for the collector-auth probe to finish before deciding whether to
  // render the CTA — avoids a flash-of-card on reload for already-linked
  // orders.
  if (authLoading) return null;
  // If order is already attached to an account OR the user is signed in,
  // don't render the CTA.
  if (collector) return null;
  if (order?.collector_id) return null;
  // Hide if final order lifecycle is cancelled.
  if (order?.status === "cancelled") return null;

  const reloadOrder = async () => {
    try {
      const fresh = await axios.get(`${API}/orders/${publicId}`, { params: { t: magicToken } });
      setOrder(fresh.data);
    } catch (_e) { /* ignore */ }
  };

  const doSignup = async () => {
    if (!firstName.trim() || !lastName.trim()) return toast.error("Enter both first and last name.");
    if (password.length < 10) return toast.error("Password must be at least 10 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/collector-auth/checkout-register`, {
        first_name: firstName, last_name: lastName,
        email, phone,
        password, confirm_password: confirm,
      }, { withCredentials: true });
      await refreshAuth();
      toast.success(`Account created. Linked ${r.data?.linked_orders ?? 0} previous order(s).`);
      await reloadOrder();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("already exists")) {
        toast.info("An account already exists — please sign in instead.");
        setMode("login");
      } else {
        toast.error(formatApiError(detail) || "Sign-up failed.");
      }
    } finally { setBusy(false); }
  };

  const doLogin = async () => {
    if (!loginPassword) return toast.error("Enter your password.");
    setBusy(true);
    try {
      await axios.post(`${API}/collector-auth/login`, {
        email, password: loginPassword, remember: true,
      }, { withCredentials: true });
      await refreshAuth();
      // Trigger the back-linking of guest orders that share this email.
      const r = await axios.post(`${API}/collector-auth/link-guest-orders`, {},
                                   { withCredentials: true });
      toast.success(`Signed in. Linked ${r.data?.linked ?? 0} previous order(s).`);
      await reloadOrder();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Sign-in failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border-4 border-[var(--ink)] bg-white p-5 md:p-6 shadow-[6px_6px_0_var(--panini-yellow)]" data-testid="guest-conversion-card">
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="text-[var(--panini-red)]" />
        <div className="kicker kicker-red">Save this order to CollectorUp</div>
      </div>
      <h3 className="font-display text-lg md:text-xl mt-1.5">
        Create an account to keep every future order in one place.
      </h3>

      {mode === "idle" && (
        <div className="mt-4 flex flex-wrap gap-3">
          <button className="btn-red text-sm" onClick={() => setMode("signup")} data-testid="guest-conv-signup-btn">
            <UserPlus size={14} /> Create Free Account
          </button>
          <button className="btn-ghost text-sm" onClick={() => setMode("login")} data-testid="guest-conv-signin-btn">
            I already have an account
          </button>
        </div>
      )}

      {mode === "signup" && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="guest-conv-signup-form">
          <TrackerInput label="First name *"   testid="gc-first"    value={firstName} onChange={setFirstName} />
          <TrackerInput label="Last name *"    testid="gc-last"     value={lastName}  onChange={setLastName} />
          <TrackerInput label="Email *"        testid="gc-email"    value={email}     onChange={setEmail}    type="email" />
          <TrackerInput label="Phone *"        testid="gc-phone"    value={phone}     onChange={setPhone} />
          <TrackerInput label="Password (min 10) *"      testid="gc-password" value={password}  onChange={setPassword} type="password" />
          <TrackerInput label="Confirm password *"       testid="gc-confirm"  value={confirm}   onChange={setConfirm}  type="password" />
          <div className="md:col-span-2 flex items-center justify-between gap-3 mt-1">
            <button className="btn-ghost text-sm" onClick={() => setMode("idle")}>Cancel</button>
            <button className="btn-red text-sm" onClick={doSignup} disabled={busy} data-testid="gc-signup-submit">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : <><UserPlus size={14} /> Create & link my orders</>}
            </button>
          </div>
        </div>
      )}

      {mode === "login" && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="guest-conv-signin-form">
          <TrackerInput label="Email *" testid="gc-login-email"    value={email}         onChange={setEmail}         type="email" />
          <TrackerInput label="Password *" testid="gc-login-password" value={loginPassword} onChange={setLoginPassword} type="password" />
          <div className="md:col-span-2 flex items-center justify-between gap-3 mt-1">
            <button className="btn-ghost text-sm" onClick={() => setMode("idle")}>Cancel</button>
            <button className="btn-red text-sm" onClick={doLogin} disabled={busy} data-testid="gc-login-submit">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Signing in…</> : "Sign in & link my orders"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TrackerInput({ label, testid, value, onChange, type = "text" }) {
  return (
    <label className="block" data-testid={`${testid}-field`}>
      <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      <input
        type={type} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
        data-testid={testid}
      />
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Phase 1b — customer-facing components
// ─────────────────────────────────────────────────────────────────────

function OrderDetailsBlock({ order, publicId, magicToken, onOrderChanged }) {
  const c = order.customer || {};
  const d = order.delivery || {};
  const submitted = order.submitted_at || order.created_at || "";
  const submittedNice = submitted ? new Date(submitted).toLocaleString() : "—";
  const [showChange, setShowChange] = React.useState(false);
  const isPickup = (d.code || "").toLowerCase() === "local_pickup";
  // Customer self-serve is legal in the three pre-fulfillment statuses
  // only. Anything past `payment_verified` (preparing / ready / shipped
  // / completed / cancelled) requires the admin flow.
  const eligibleStatus = ["awaiting_payment", "payment_submitted", "payment_verified"]
    .includes(order.status);
  const canChangeToDelivery = isPickup && eligibleStatus;
  return (
    <section className="mt-6 p-5 border-2 border-[var(--ink)] rounded-2xl bg-white" data-testid="order-details-block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">Order details</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-sm">
        <Detail label="Customer name" value={c.name || "—"} testid="detail-name" />
        <Detail label="Email" value={c.email || "—"} testid="detail-email" />
        <Detail label="Phone" value={c.phone || "—"} testid="detail-phone" />
        <Detail label="Album" value={order.album_slug || "—"} testid="detail-album" />
        <Detail label="Delivery method" value={d.label || d.code || "—"} testid="detail-delivery" />
        <Detail label="Submitted" value={submittedNice} testid="detail-submitted" />
        <Detail label="Order number" value={order.public_id} mono testid="detail-order-number" />
      </div>
      {canChangeToDelivery && (
        <div className="mt-4">
          <button
            type="button" onClick={() => setShowChange(true)}
            className="btn bg-[var(--panini-red)] text-white text-sm"
            data-testid="cta-change-to-delivery"
          >
            Change to Delivery
          </button>
          <p className="mt-2 text-xs text-[var(--smoke)]">
            Ship this order via USPS Stamp (+$4.00) or UPS Ground (+$12.00).
            Your sticker subtotal and tax stay the same.
          </p>
        </div>
      )}
      {showChange && (
        <ChangeToDeliveryModal
          open={showChange}
          onClose={() => setShowChange(false)}
          order={order}
          magicToken={magicToken}
          publicId={publicId}
          onChanged={onOrderChanged}
        />
      )}
    </section>
  );
}

function Detail({ label, value, mono = false, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">{label}</div>
      <div className={"mt-0.5 " + (mono ? "font-mono font-bold" : "")} data-testid={testid}>{value}</div>
    </div>
  );
}

function PartialPreferenceCard({ order, magicToken, publicId, onSaved }) {
  const isPickup = (order.delivery?.code || "").toLowerCase() === "local_pickup";
  const current = order.partial_preference?.choice || "";
  const [choice, setChoice] = useState(current);
  const [busy, setBusy] = useState(false);
  const summary = order.fulfillment_summary || {};

  const save = async (next) => {
    setChoice(next);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/orders/${publicId}/partial-preference`,
        { magic_token: magicToken, choice: next });
      onSaved({ ...order, partial_preference: r.data.partial_preference });
      toast.success("Preference saved.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not save preference.");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-6 p-5 border-2 border-[var(--ink)] rounded-2xl bg-white" data-testid="partial-preference-card">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
        Some stickers are still pending
      </div>
      <p className="text-sm mt-2">
        We have {Number(summary.ready_total || 0)} ready and {Number(summary.pending_total || 0)} still pending.
        Would you like to {isPickup ? "pick up" : "receive"} what&apos;s ready now, or wait until everything&apos;s in?
      </p>
      <div className="mt-4 flex flex-col gap-2">
        <label className={"flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer " + (choice === "wait" ? "border-[var(--ink)] bg-[var(--panini-yellow-soft)]" : "border-neutral-300")}>
          <input
            type="radio"
            name="partial"
            value="wait"
            checked={choice === "wait"}
            disabled={busy}
            onChange={() => save("wait")}
            data-testid="partial-choice-wait"
            className="mt-1"
          />
          <div>
            <div className="font-bold text-sm">Wait until everything is ready</div>
            <div className="text-xs text-[var(--smoke)] mt-0.5">
              We&apos;ll notify you when every sticker is in.
            </div>
          </div>
        </label>
        <label className={"flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer " + (choice === "partial" ? "border-[var(--ink)] bg-[var(--panini-yellow-soft)]" : "border-neutral-300")}>
          <input
            type="radio"
            name="partial"
            value="partial"
            checked={choice === "partial"}
            disabled={busy}
            onChange={() => save("partial")}
            data-testid="partial-choice-partial"
            className="mt-1"
          />
          <div>
            <div className="font-bold text-sm">
              {isPickup ? "Pick up available stickers now" : "Ship available stickers now"}
            </div>
            <div className="text-xs text-[var(--smoke)] mt-0.5">
              {isPickup
                ? "We'll set aside what's ready for you today. The rest goes out later."
                : "We'll ship what's ready today. If a second shipment is needed later, additional shipping charges may apply."}
            </div>
          </div>
        </label>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────
//  Phase 1c — WhatsApp payment-verification button (customer)
//
//  Fetches the configurable business WhatsApp number from
//  /api/public/settings and builds a wa.me deep-link with a pre-filled
//  message that references the current order number. Renders ONLY in
//  the payment_submitted state — Phase 1a polling automatically hides
//  this once the admin verifies the payment.
// ─────────────────────────────────────────────────────────────────────
function WhatsAppVerifyButton({ orderNumber }) {
  const [waHref, setWaHref] = React.useState("");

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/settings`);
        const raw = r.data?.whatsapp_business_number || "";
        const digits = String(raw).replace(/\D/g, "");
        if (!digits) return;
        const msg =
          `Hello, I just placed an order on CollectorUp.com.\n` +
          `Order number: ${orderNumber}\n` +
          `I have submitted my payment. Please confirm my payment verification.\n` +
          `Thank you!`;
        const url = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
        if (alive) setWaHref(url);
      } catch (_e) { /* silent — button just doesn't render */ }
    })();
    return () => { alive = false; };
  }, [orderNumber]);

  if (!waHref) return null;
  return (
    <a
      href={waHref}
      target="_blank"
      rel="noreferrer"
      className="mt-5 inline-flex items-center gap-2 rounded-xl border-2 border-emerald-700 bg-emerald-600 text-white text-sm px-4 py-2.5 font-bold shadow-[3px_3px_0_var(--ink)] hover:shadow-[5px_5px_0_var(--ink)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition"
      data-testid="btn-whatsapp-verify-payment"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.52 3.48A11.87 11.87 0 0012.02 0C5.42 0 .04 5.38.04 12c0 2.11.55 4.15 1.6 5.97L0 24l6.19-1.62A11.9 11.9 0 0012.02 24c6.6 0 11.98-5.38 11.98-12 0-3.2-1.24-6.21-3.48-8.52zM12.02 21.82a9.8 9.8 0 01-5-1.37l-.36-.21-3.67.96.98-3.58-.24-.37a9.79 9.79 0 01-1.5-5.24c0-5.42 4.42-9.83 9.85-9.83 2.63 0 5.1 1.03 6.95 2.89a9.75 9.75 0 012.88 6.95c0 5.42-4.42 9.8-9.84 9.8zm5.63-7.32c-.31-.15-1.83-.9-2.11-1-.28-.1-.49-.15-.7.15-.2.31-.8 1-1 1.2-.19.2-.37.22-.68.07-.31-.15-1.3-.48-2.48-1.53a9.28 9.28 0 01-1.72-2.13c-.18-.31 0-.47.14-.62.14-.14.31-.37.47-.55.16-.19.21-.32.32-.53.11-.21.05-.4-.02-.55-.08-.15-.7-1.69-.96-2.31-.25-.61-.51-.53-.7-.54h-.6c-.2 0-.53.08-.81.4-.28.31-1.07 1.05-1.07 2.56s1.1 2.97 1.25 3.17c.15.2 2.16 3.3 5.24 4.63 3.08 1.33 3.08.88 3.64.83.55-.05 1.83-.75 2.09-1.47.26-.72.26-1.34.18-1.47-.08-.13-.28-.2-.59-.35z"/>
      </svg>
      Message us on WhatsApp — Request payment verification
    </a>
  );
}



/**
 * PF4 · FulfillmentProgressCard — customer-facing derived fulfillment
 * status + per-shipment history. Reads `order.fulfillment_display` and
 * `order.fulfillments` (both populated by the backend `get_tracker()`
 * projection). Voided events are already stripped server-side; per-code
 * allocations are collapsed to `item_count` for clarity.
 */
function FulfillmentProgressCard({ display, fulfillments }) {
  const [expanded, setExpanded] = React.useState({});
  if (!display) return null;
  // Hide when there is nothing helpful to say (draft/preparing without progress).
  const hidePillStates = new Set(["preparing", "cancelled"]);
  const showBanner = !!display.summary_text
                     || (fulfillments && fulfillments.length > 0)
                     || !hidePillStates.has(display.state);
  if (!showBanner) return null;
  const pickupCount = fulfillments.filter((f) => f.method === "pickup").length;
  const shipCount = fulfillments.filter((f) => f.method === "shipping").length;
  let pickupIdx = 0;
  let shipIdx = 0;
  const items = fulfillments.map((f) => {
    const isShip = f.method === "shipping";
    const number = isShip ? ++shipIdx : ++pickupIdx;
    const label = isShip ? `Shipment ${number}` : `Pickup ${number}`;
    return { ...f, label };
  });
  return (
    <div
      className="mt-6 p-5 border-2 border-[var(--ink)] rounded-2xl bg-white shadow-[6px_6px_0_var(--ink)]"
      data-testid="tracker-fulfillment-progress"
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]"
           data-testid="tracker-fulfillment-label">
        {display.label}
      </div>
      {display.summary_text && (
        <p className="mt-2 text-base font-bold text-[var(--ink)]"
           data-testid="tracker-fulfillment-summary">
          {display.summary_text}
        </p>
      )}
      {display.mixed_method
        && (display.pickup_total > 0 || display.shipping_total > 0) && (
        <p className="mt-1 text-xs text-slate-600"
           data-testid="tracker-fulfillment-mixed-detail">
          {display.pickup_total > 0 && `${display.pickup_total} picked up`}
          {display.pickup_total > 0 && display.shipping_total > 0 && " · "}
          {display.shipping_total > 0 && `${display.shipping_total} shipped`}
        </p>
      )}

      {items.length > 0 && (
        <ul className="mt-4 divide-y border-t" data-testid="tracker-fulfillment-list">
          {items.map((f) => (
            <li key={f.id} className="py-3" data-testid={`tracker-fulfillment-${f.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <button
                    className="text-sm font-semibold text-[var(--ink)] flex items-center gap-1"
                    onClick={() => setExpanded((e) => ({ ...e, [f.id]: !e[f.id] }))}
                    data-testid={`tracker-fulfillment-toggle-${f.id}`}
                  >
                    {expanded[f.id] ? "▾" : "▸"} {f.label}
                  </button>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {f.handed_over_at && new Date(f.handed_over_at).toLocaleDateString(undefined, {
                      month: "short", day: "numeric", year: "numeric",
                    })}
                    {" · "}
                    <span className="font-mono">{f.item_count} sticker{f.item_count === 1 ? "" : "s"}</span>
                    {f.carrier && <span className="ml-2">· {f.carrier}</span>}
                  </div>
                  {f.method === "shipping" && (
                    f.tracking_number ? (
                      f.tracking_url ? (
                        <a
                          href={f.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-block text-xs font-mono underline decoration-dotted text-[var(--panini-red)]"
                          data-testid={`tracker-fulfillment-tracking-${f.id}`}
                        >
                          Tracking {f.tracking_number}
                        </a>
                      ) : (
                        <span className="mt-1 inline-block text-xs font-mono text-slate-700"
                              data-testid={`tracker-fulfillment-tracking-${f.id}`}>
                          Tracking {f.tracking_number}
                        </span>
                      )
                    ) : (
                      <span className="mt-1 inline-block text-xs text-slate-500"
                            data-testid={`tracker-fulfillment-no-tracking-${f.id}`}>
                        No tracking available
                      </span>
                    )
                  )}
                </div>
              </div>
              {expanded[f.id] && (f.allocations || []).length > 0 && (
                <ul className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5 text-xs font-mono text-slate-700"
                    data-testid={`tracker-fulfillment-allocations-${f.id}`}>
                  {f.allocations.map((a, i) => (
                    <li key={i}>{a.code} × {a.qty}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
